import { Badge, BanCircleIcon, CreditCardIcon, Icon, StarIcon, Tooltip } from "evergreen-ui";

import { useListActiveOrderFlagsQuery } from "../../../v2/features/orderFlags/orderFlags.api";
import { matchOrderFlags } from "../../../v2/features/orderFlags/lib/matchFlags";
import { ORDER_TAGS, renderOrderTagBadge } from "./orderTagLegend";
import CountryFlag from "./CountryFlag";

// Wrap a legend badge in its Romanian tooltip. The span gives evergreen's
// Tooltip a ref target and hugs the badge (inline-flex).
const TagBadge = ({ tagKey, label }) => (
  <Tooltip content={ORDER_TAGS[tagKey].tooltip}>
    <span style={{ display: "inline-flex" }}>{renderOrderTagBadge(tagKey, { label })}</span>
  </Tooltip>
);

export const PaymentBlock = (props) => (
  <Badge
    {...props}
    color={
      props.paymentStatus === "unpaid"
        ? "red"
        : props.paymentStatus === "partial_payment"
        ? "orange"
        : props.paymentStatus === "paid"
        ? "green"
        : props.paymentStatus === "payment_failed"
        ? "purple"
        : "neutral"
    }
  >
    {props.paymentType && props.paymentType.handler === 'card-gateway' && (
      <Icon icon={CreditCardIcon} size={12} marginRight={5} />
    )}
    {props.paymentStatusText}
  </Badge>
);

export const StatusBlock = (props) => (
  <Badge
    {...props}
    color={
      props.status === "placed"
        ? "red"
        : props.status === "confirmed"
        ? "orange"
        : props.status === "processed"
        ? "yellow"
        : props.status === "delivered"
        ? "teal"
        : props.status === "completed"
        ? "green"
        : props.status === "cancelled"
        ? "neutral"
        : props.status === "returning"
        ? "teal"
        : props.status === "returned"
        ? "purple"
        : "neutral"
    }
  >
    {props.statusText}
  </Badge>
);

export const TypeBlock = (props) => (
  <Badge
    {...props}
    color={
      props.orderType === "online"
        ? "blue"
        : props.orderType === "glovo"
        ? "yellow"
        : props.orderType === "phone"
        ? "blue"
        : props.orderType === "email"
        ? "blue"
        : props.orderType === "fizic"
        ? "purple"
        : "neutral"
    }
  >
    {props.orderTypeText}
  </Badge>
);

export const PreorderBlock = (props) => {
  return props.isPreorder === 1 ? <TagBadge tagKey="PREORDER" /> : null;
};


export const BackorderBlock = (props) => {
  return props.isBackorder === 1 ? <TagBadge tagKey="BACKORDER" /> : null;
};

export const SinglesBlock = (props) => {
  let singles = false;
  let singlesCount = 0;
  for (let i in props.items) {
    if (props.items[i].type === "card") {
      singles = true;
      singlesCount += 1;
    }

    if(singlesCount > 99) {
      break;
    }
  }

  return singles ? <TagBadge tagKey="SINGLES" label={`S${singlesCount >= 99 ? '99+' : singlesCount}`} /> : null;
};

// Do-not-deliver flags now live in the DB (v2 "Lista neagra livrare"). We match
// the order's email / billing phone against the active set; an order can carry
// more than one tag (e.g. matched by both email and phone).
export const AlertBlock = (props) => {
  const { data: flags } = useListActiveOrderFlagsQuery();
  const matched = matchOrderFlags(flags, props.email, props.billingPhoneNumber);
  if (matched.length === 0) return null;

  return (
    <>
      {matched.map((tag) => (
        <Tooltip key={tag.code} content={tag.description || "Lista neagra — NU livra."}>
          <span style={{ display: "inline-flex" }}>
            <Badge style={{ backgroundColor: tag.color || "#1f2937", color: "white" }}>
              <Icon icon={BanCircleIcon} size={12} marginRight={4} />
              {tag.label}
            </Badge>
          </span>
        </Tooltip>
      ))}
    </>
  );
};

export const CompanyBlock = (props) => {
  if(props.companyName) {
    return <TagBadge tagKey="B2B" />
  }
}

// External (non-Romania) order — flagged with the destination country's actual
// flag (drawn SVG, see CountryFlag) plus its code, so the list shows where it's
// headed at a glance.
export const ExternalBlock = (props) => {
  const country = props.deliveryCountry;
  if (!country || country === "RO") return null;
  return (
    <Tooltip content={ORDER_TAGS.EXTERNAL.tooltip}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 6px",
          borderRadius: 4,
          background: "#EDEFF5",
          color: "#474D66",
          fontSize: 11,
          fontWeight: 600,
          verticalAlign: "middle",
        }}
      >
        <CountryFlag code={country} />
        {country}
      </span>
    </Tooltip>
  );
};

export const StorePickupBlock = (props) => {
  if (props.delivery === "personal" && props.locationId !== 2) {
    return <TagBadge tagKey="STORE_PL" />;
  } else if(props.delivery === "personal-brasov" || (props.delivery === 'personal' && props.locationId === 2)) {
    return <TagBadge tagKey="STORE_BV" />;
  } else if(props.delivery === "delivery-locker") {
    return <TagBadge tagKey="LOCKER" />;
  }

  return null;
};

export const EntityBlock = (props) => (
  <Badge
    {...props}
    colors={
      props.entityType === "pf"
        ? "teal"
        : props.entityType === "pj"
        ? "purple"
        : "neutral"
    }
  >
    {props.entityTypeText}
  </Badge>
);

export const DeliveryBlock = (props) => (
  <Badge
    {...props}
    colors={
      props.delivery === "delivery"
        ? "blue"
        : props.delivery === "personal"
        ? "green"
        : "neutral"
    }
  >
    {props.deliveryText}
  </Badge>
);

export const ReviewBlock = (props) => {
  if (props.review && props.review.rating) {
    return (
      <Badge style={{ backgroundColor: '#fbbf24', color: '#333' }}>
        <Icon icon={StarIcon} size={12} marginRight={4}/>
        {props.review.rating}
      </Badge>
    );
  }
  // Show gray star if no review
  return (
    <Icon icon={StarIcon} size={12} color="#9CA3AF" title="Fără recenzie"/>
  );
};
